import type { AdaptiveLogger, GraphDriveItem } from "@1js/owl-service";
import {
  getDriveItem,
  getParamStatement,
  getSharesUrl,
  LogCategory,
  TraceLevel,
  WacParams,
} from "@1js/owl-service";
import { fetchAccessToken } from "../frame";
import {
  type AccessTokenFunction,
  type ShareUrlBootDocInfo,
  type HttpHeaders,
} from "../types";

export async function getDriveItemWithShareLink(
  sharedUrl: string,
  getAuthToken: AccessTokenFunction,
  logger?: AdaptiveLogger
): Promise<ShareUrlBootDocInfo> {
  let headers: HttpHeaders = {
    accept: "application/json",
    prefer: "redeemSharingLink,allow-zerobyte,manualRedirect",
  };

  // Retrieve auth token before calling vroom
  const parsedUrl = new URL(sharedUrl);
  const resourceUrl = `${parsedUrl.protocol}//${parsedUrl.host}`;
  const tokenResponse = await fetchAccessToken(getAuthToken, resourceUrl);
  if (!tokenResponse.success) {
    return Promise.reject(
      `GetDriveItemWithShareLink: Failed to fetch access token with resourceUrl: ${tokenResponse.error}`
    );
  }
  headers["Authorization"] = `${"Bearer "}${tokenResponse.token}`.trim();
  const httpRequestOptions = { headers };

  // Use action open to retrieve drive items via SPO vroom call
  const queryParams = [getParamStatement(WacParams), `action=open`]
    .filter((x) => !!x)
    .join("&");
  const shareUrl = getSharesUrl(sharedUrl);
  const endpoint = `${shareUrl}?${queryParams}`;
  const baseUrl = `${resourceUrl}${"/_api/v2.0"}`;
  const passUrl = `${baseUrl}/${endpoint}`;

  return getDriveItem(
    passUrl,
    httpRequestOptions,
    false /* forceFetch */,
    logger
  )
    .then((driveItem: GraphDriveItem) => {
      return {
        siteUrl: driveItem.sharepointIds.siteUrl,
        listItemUniqueId: driveItem.sharepointIds.listItemUniqueId,
        vroomAccessToken: tokenResponse.token,
        appName: driveItem.openWith?.wac?.appName, // TODO: identify app in another way
      };
    })
    .catch(async (error) => {
      if (
        error &&
        error.status === 401 &&
        error.claimsError === "insufficient_claims" &&
        error.claims
      ) {
        logger?.ULS.sendTraceTag(
          0x1e318295 /* tag_4mykv */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Important,
          `GetDriveItemWithShareLink: Refetch access token with claim due to claim challenge.`
        );
        const tokenResponse = await fetchAccessToken(
          getAuthToken,
          resourceUrl,
          error.claims
        );
        if (!tokenResponse.success) {
          return Promise.reject(
            `GetDriveItemWithShareLink: Failed fetch access token with claim: ${tokenResponse.error}`
          );
        }
        headers["Authorization"] = `${"Bearer "}${tokenResponse.token}`.trim();
        return getDriveItem(
          passUrl,
          httpRequestOptions,
          false /* forceFetch */,
          logger
        )
          .then((driveItem: GraphDriveItem) => {
            return {
              siteUrl: driveItem.sharepointIds.siteUrl,
              listItemUniqueId: driveItem.sharepointIds.listItemUniqueId,
              vroomAccessToken: tokenResponse.token,
              appName: driveItem.openWith?.wac?.appName, // TODO: identify app in another way
            };
          })
          .catch((error) => {
            return Promise.reject(
              `Failed to fetch drive item by vroom call: ${error}`
            );
          });
      }
      return Promise.reject(
        `Failed to fetch drive item by vroom request: ${error}`
      );
    });
}
